export type Locale = 'en' | 'da';

export const translations = {
  en: {
    common: {
      skipToContent: 'Skip to Content',
      backToHome: 'Back to home',
    },
    nav: {
      about: 'About',
      experience: 'Experience',
      blog: 'Blog',
      projects: 'Projects',
    },
    home: {
      about: {
        title: 'About',
        intro: "Hey yall 👋 I'm an American living in Denmark who has been building software for over a decade.",
        body: "I have a knack for improving developer experience and building platform tooling that helps teams ship reliably at scale. More recently, my work has focused on utilizing AI for internal developer tooling, with an emphasis on helping teams move AI-assisted code changes to production faster.",
        outro: "When I'm not building software, you can find me hiking, gaming, or tinkering with my Home Assistant setup.",
      },
      experience: {
        title: 'Experience',
        viewAll: 'View all experience →',
      },
      blog: {
        title: 'Blog',
        viewAll: 'View all posts →',
      },
      projects: {
        title: 'Projects',
        vejen: {
          name: 'Vejen til Dansk Statsborgerskab',
          description: 'A project I made to help myself pass both the citizenship exam and the naturalisation exam on my path to Danish citizenship. Automated ingestion of new exams with Node for parsing and LLMs for quality checks and categorisation.',
        },
        fatties: {
          name: 'FATTIES NFT Platform',
          description: 'Full-stack NFT minting platform with smart contracts, web interface, and backend services. Includes presale and public minting, whitelist management, IPFS metadata storage, and real-time contract state tracking.',
        },
      },
    },
    blogList: {
      title: 'Blog',
      sort: 'Sort',
      newestFirst: 'Newest first',
      oldestFirst: 'Oldest first',
      year: 'Year',
      all: 'All',
      tags: 'Tags',
      noPosts: 'No posts match the current filters.',
      pageOf: 'Page {current} of {total}',
      previous: 'Previous',
      next: 'Next',
    },
    resume: {
      title: 'Experience',
      backToHome: 'Back to home',
      viewAll: 'View all experience →',
    },
    fattiesProject: {
      metaTitle: 'FATTIES NFT Platform',
      metaDescription: 'Full-stack NFT minting platform with smart contracts, web interface, and backend services. Portfolio project.',
      title: 'FATTIES NFT Platform',
      subtitle: 'Full-stack NFT minting platform with smart contracts, web interface, and backend services',
      disclaimer: 'Note: The FATTIES site is no longer being hosted.',
      screenshotAlt: 'FATTIES NFT Platform Screenshot',
      totalSupply: 'Total Supply',
      twitterFollowers: 'Twitter Followers',
      instagramFollowers: 'Instagram Followers',
      launchDate: 'Launch Date',
      description: 'Full-stack NFT minting platform with smart contracts, web interface, and backend services. Includes presale and public minting, whitelist management, IPFS metadata storage, and real-time contract state tracking. Features a React frontend with Web3 integration, Express API for metadata and minting operations, and a Discord bot for community management.',
      builtWith: 'Built With',
      openSea: 'FATTIES on OpenSea',
      instagram: 'FATTIES on Instagram',
      twitter: 'FATTIES on Twitter',
    },
  },
  da: {
    common: {
      skipToContent: 'Spring til indhold',
      backToHome: 'Tilbage til forsiden',
    },
    nav: {
      about: 'Om',
      experience: 'Erfaring',
      blog: 'Blog',
      projects: 'Projekter',
    },
    home: {
      about: {
        title: 'Om',
        intro: 'Hej 👋 Jeg er amerikaner og bor i Danmark og har bygget software i mere end et årti.',
        body: 'Jeg er god til at forbedre developer experience og bygge platformværktøj, der hjælper teams med at levere pålideligt i skala. Senest har mit arbejde fokuseret på at bruge AI til intern developer tooling, med vægt på at få AI-understøttede kodeændringer hurtigere i produktion.',
        outro: 'Når jeg ikke bygger software, kan du finde mig på vandreture, i spil eller med at pille ved mit Home Assistant-setup.',
      },
      experience: {
        title: 'Erfaring',
        viewAll: 'Se al erfaring →',
      },
      blog: {
        title: 'Blog',
        viewAll: 'Se alle indlæg →',
      },
      projects: {
        title: 'Projekter',
        vejen: {
          name: 'Vejen til Dansk Statsborgerskab',
          description: 'Et projekt jeg lavede for at hjælpe mig selv med at bestå både medborgerskabsprøven og indfødsretsprøven på min vej til dansk statsborgerskab. Automatiseret indtagelse af nye eksamener med Node til parsing og LLMs til kvalitetstjek og kategorisering.',
        },
        fatties: {
          name: 'FATTIES NFT Platform',
          description: 'Full-stack NFT minting-platform med smart contracts, webinterface og backend. Inkluderer presale og public minting, whitelist-håndtering, IPFS-metadata og realtidssporing af kontraktstatus.',
        },
      },
    },
    blogList: {
      title: 'Blog',
      sort: 'Sortering',
      newestFirst: 'Nyeste først',
      oldestFirst: 'Ældste først',
      year: 'År',
      all: 'Alle',
      tags: 'Tags',
      noPosts: 'Ingen indlæg matcher de valgte filtre.',
      pageOf: 'Side {current} af {total}',
      previous: 'Forrige',
      next: 'Næste',
    },
    resume: {
      title: 'Erfaring',
      backToHome: 'Tilbage til forsiden',
      viewAll: 'Se al erfaring →',
    },
    fattiesProject: {
      metaTitle: 'FATTIES NFT Platform',
      metaDescription: 'Full-stack NFT minting-platform med smart contracts, webinterface og backend. Porteføljeprojekt.',
      title: 'FATTIES NFT Platform',
      subtitle: 'Full-stack NFT minting-platform med smart contracts, webinterface og backend',
      disclaimer: 'Bemærk: FATTIES-sitet hostes ikke længere.',
      screenshotAlt: 'FATTIES NFT Platform-skærmbillede',
      totalSupply: 'Samlet udbud',
      twitterFollowers: 'Twitter-følgere',
      instagramFollowers: 'Instagram-følgere',
      launchDate: 'Lanceringsdato',
      description: 'Full-stack NFT minting-platform med smart contracts, webinterface og backend. Inkluderer presale og public minting, whitelist-håndtering, IPFS-metadata og realtidssporing af kontraktstatus. React-frontend med Web3-integration, Express API til metadata og minting, samt Discord-bot til fællesskabsstyring.',
      builtWith: 'Bygget med',
      openSea: 'FATTIES på OpenSea',
      instagram: 'FATTIES på Instagram',
      twitter: 'FATTIES på Twitter',
    },
  },
} as const;

export type TranslationKey = string;

function getNested(obj: Record<string, unknown>, path: string): string | undefined {
  const keys = path.split('.');
  let current: unknown = obj;
  for (const key of keys) {
    if (current == null || typeof current !== 'object') return undefined;
    current = (current as Record<string, unknown>)[key];
  }
  return typeof current === 'string' ? current : undefined;
}

export function translate(locale: Locale, key: TranslationKey, params?: Record<string, string | number>): string {
  const obj = translations[locale] as Record<string, unknown>;
  let value = getNested(obj, key) ?? getNested(translations.en as Record<string, unknown>, key) ?? key;
  if (params) {
    Object.entries(params).forEach(([k, v]) => {
      value = value.replace(new RegExp(`\\{${k}\\}`, 'g'), String(v));
    });
  }
  return value;
}
